import { Component, Input, Output, EventEmitter, OnInit, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ChatService } from '../../../../service/chat.service';
import { alertModalWithoutConfirm, alertModalDelete, alertModalExit, alertModalRemove, alertModalConfirm, alertModalClearChat } from '../../../helpers/alert';
import { downloadFile } from '../../../helpers/helper';
import { MatDialog } from '@angular/material/dialog';
import { openDocumentPreview } from '../../document-preview-dialog.component';
import { UserService } from '../../../../service/user.service';
import {
  getFilesFromMessage,
  isImage,
  isVideo,
  isAudio,
  isVoiceMessage,
  isDocument,
  isGifMessage,
  isURL,
  extractUrls,
  getMessageMeta
} from '../../utils/helpers.utils';

@Component({
  selector: 'app-group-info-panel',
  standalone: true,
  imports: [FormsModule, CommonModule],
  templateUrl: './group-info-panel.component.html',
  styleUrls: ['./group-info-panel.component.css']
})
export class GroupInfoPanelComponent implements OnInit, OnChanges, OnDestroy {
  @Input() isGroupInfoOpen: boolean = false;
  @Input() userId: any;
  @Input() memberChannelId: string = '';
  @Input() groupName: string = '';
  @Input() groupChatName: string = '';
  @Input() groupChatDescription: string = '';
  @Input() groupChatProfileImg: string = '';
  @Input() allGroupMembers: any[] = [];
  @Input() totalGroupMembers: any;
  @Input() exitedGroups: Set<string> = new Set();
  @Input() isGroup: boolean = true;
  @Input() singleUser: any = null;
  @Input() singleChatUsername: string = '';
  @Input() singleUserProfileImg: string = '';
  @Input() chatUserStatus: string = '';
  @Input() isChatUserOnline: boolean = false;
  @Input() chatMessages: any[] = [];
  @Input() activeChannelId: string = '';

  @Output() isGroupInfoOpenChange = new EventEmitter<boolean>();
  @Output() allGroupMembersChange = new EventEmitter<any[]>();
  @Output() exitedGroupsChange = new EventEmitter<Set<string>>();
  @Output() startSingleChat = new EventEmitter<string>();
  @Output() closeGroupInfoPanelEvent = new EventEmitter<void>();
  @Output() groupNameChange = new EventEmitter<string>();
  @Output() groupChatNameChange = new EventEmitter<string>();
  @Output() groupChatDescriptionChange = new EventEmitter<string>();
  @Output() groupChatProfileImgChange = new EventEmitter<string>();
  @Output() chatCleared = new EventEmitter<void>();
  @Output() groupExited = new EventEmitter<string>();
  @Output() groupDeleted = new EventEmitter<string>();
  @Output() mediaClick = new EventEmitter<{ message: any; index: number }>();
  @Output() clearSingleChat = new EventEmitter<void>();

  // Internal state
  panelView: 'info' | 'member' | 'add' = 'info';
  viewingMember: any = null;
  expandedMemberId: string | null = null;
  isEditGroupMode: boolean = false;
  selectedGroupImageFile: File | null = null;
  groupMemberSearchQuery: string = "";
  addMemberSearchTerm: string = "";
  selectedNewMembers: any[] = [];
  availableUsersForAdd: any[] = [];
  newGroupName: string = "";
  newDiscription: string = "";
  newGroupImage: string = "";
  chatLoader: boolean = false;
  isAddingMember: boolean = false;
  isCreatingGroup: boolean = false;
  isDescriptionExpanded: boolean = false;

  // Shared content state
  selectedSharedTab: 'members' | 'media' | 'files' | 'audio' | 'gifs' | 'links' = 'members';
  sharedMedia: any[] = [];
  sharedFiles: any[] = [];
  sharedAudio: any[] = [];
  sharedGifs: any[] = [];
  sharedLinks: any[] = [];
  currentPlayingAudioUrl: string | null = null;
  audioPlayer: HTMLAudioElement | null = null;
  isPlayingAudio: boolean = false;
  audioProgress: number = 0;
  audioCurrentTime: string = '0:00';

  constructor(
    private chatService: ChatService,
    private sanitizer: DomSanitizer,
    private userService: UserService,
    private dialog: MatDialog
  ) { }

  ngOnInit() {
    this.selectedSharedTab = this.isGroup ? 'members' : 'media';
  }

  ngOnDestroy() {
    this.stopAudioPlayback();
  }

  get singleUserEmail(): string {
    return (
      this.singleUser?.emailId ||
      this.singleUser?.email ||
      this.singleUser?.mail ||
      this.singleUser?.email_id ||
      ''
    );
  }

  private fetchMissingUserDetails(): void {
    if (this.isGroup || !this.singleUser?.userId || this.singleUserEmail) return;
    const uid = this.singleUser.userId;
    this.userService.getUserDetails(uid).subscribe({
      next: (res: any) => {
        const data = res?.data || res;
        const email = data?.emailId || data?.email || data?.mail || data?.email_id;
        if (email && this.singleUser && this.singleUser.userId === uid) {
          this.singleUser = {
            ...this.singleUser,
            emailId: email,
            email: email,
            phone: this.singleUser.phone || data?.phone || data?.mobile,
            about: this.singleUser.about || data?.about || data?.bio
          };
        }
      },
      error: (err) => {
        console.warn('Could not fetch user details in group info panel:', err);
      }
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['isGroupInfoOpen']) {
      if (changes['isGroupInfoOpen'].currentValue) {
        // Reset panel state when opened
        this.panelView = 'info';
        this.viewingMember = null;
        this.isEditGroupMode = false;
        this.groupMemberSearchQuery = "";
        this.addMemberSearchTerm = "";
        this.selectedNewMembers = [];
        this.isDescriptionExpanded = false;
        this.selectedSharedTab = this.isGroup ? 'members' : 'media';
        this.extractSharedContent();
        const cid = this.isGroup ? this.memberChannelId : (this.activeChannelId || this.singleUser?.channelId);
        if (cid) {
          this.fetchSharedHistory(cid);
        }
        this.fetchMissingUserDetails();
      } else {
        this.stopAudioPlayback();
      }
    }
    if (changes['singleUser']) {
      this.fetchMissingUserDetails();
    }
    if (changes['isGroup']) {
      if (!this.isGroup && this.selectedSharedTab === 'members') {
        this.selectedSharedTab = 'media';
      } else if (this.isGroup && !this.selectedSharedTab) {
        this.selectedSharedTab = 'members';
      }
    }
    if (changes['chatMessages'] && this.isGroupInfoOpen) {
      this.extractSharedContent();
    }
    if (changes['memberChannelId'] || changes['groupChatDescription']) {
      this.isDescriptionExpanded = false;
    }
  }

  get isDescriptionLong(): boolean {
    if (!this.groupChatDescription) return false;
    const desc = this.groupChatDescription.trim();
    return desc.length > 120 || desc.split('\n').length > 3;
  }

  toggleDescription(event?: MouseEvent): void {
    if (event) {
      event.stopPropagation();
    }
    this.isDescriptionExpanded = !this.isDescriptionExpanded;
  }

  get formattedGroupDescription(): SafeHtml {
    if (!this.groupChatDescription) return '';
    const escaped = this.escapeHtml(this.groupChatDescription);
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const withLinks = escaped.replace(urlRegex, (url) => {
      let cleanUrl = url;
      let trailing = '';
      const punctuation = [',', '.', '!', '?', ')', ']', '}'];
      while (cleanUrl.length > 0 && punctuation.includes(cleanUrl[cleanUrl.length - 1])) {
        if (cleanUrl[cleanUrl.length - 1] === ')' && cleanUrl.includes('(')) break;
        if (cleanUrl[cleanUrl.length - 1] === ']' && cleanUrl.includes('[')) break;
        if (cleanUrl[cleanUrl.length - 1] === '}' && cleanUrl.includes('{')) break;
        trailing = cleanUrl[cleanUrl.length - 1] + trailing;
        cleanUrl = cleanUrl.substring(0, cleanUrl.length - 1);
      }
      return `<a href="${cleanUrl}" target="_blank" rel="noopener noreferrer" class="desc-link" (click)="$event.stopPropagation()">${cleanUrl}</a>${trailing}`;
    });
    return this.sanitizer.bypassSecurityTrustHtml(withLinks);
  }

  escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  autoGrowDescription(event: any): void {
    const el = event?.target || event;
    if (el) {
      el.style.height = 'auto';
      el.style.height = Math.max(38, el.scrollHeight) + 'px';
    }
  }

  // Getters
  get activeGroupMembersCount(): number {
    return this.allGroupMembers.filter(
      m => !m.isRemoved && !m.leftGroup
    ).length;
  }

  get isCurrentGroupExited(): boolean {
    if (!this.memberChannelId) return false;
    if (this.exitedGroups && this.exitedGroups.has(this.memberChannelId)) return true;
    if (!this.allGroupMembers) return false;
    const me = this.allGroupMembers.find((m) => m.userId === this.userId);
    return !!(me && (me.leftGroup || me.isRemoved));
  }

  get filteredAvailableUsersForAdd() {
    let users = this.availableUsersForAdd;

    if (this.addMemberSearchTerm) {
      const term = this.addMemberSearchTerm.toLowerCase();
      users = users.filter(u =>
        (u.userName && u.userName.toLowerCase().includes(term)) ||
        (u.displayName && u.displayName.toLowerCase().includes(term))
      );
    }

    // Sort alphabetically by userName
    return users.sort((a, b) => {
      const nameA = (a.userName || '').toLowerCase();
      const nameB = (b.userName || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });
  }

  // Methods
  closeGroupInfoPanel() {
    this.isGroupInfoOpen = false;
    this.emitIsGroupInfoOpenChange();
    this.closeGroupInfoPanelEvent.emit();
  }

  emitIsGroupInfoOpenChange() {
    this.isGroupInfoOpenChange.emit(this.isGroupInfoOpen);
  }

  openMemberInfo(member: any) {
    this.viewingMember = member;
    this.panelView = 'member';
  }

  openAddMembersView() {
    this.panelView = 'add';
    this.addMemberSearchTerm = '';
    this.selectedNewMembers = [];
    this.fetchAvailableUsersForAdd();
  }

  backToGroupInfo() {
    this.panelView = 'info';
    this.viewingMember = null;
    this.selectedNewMembers = [];
  }

  dismissGroupAdmin(member: any) {
    alertModalConfirm(`Dismiss ${member.displayName || member.userName} as admin?`).then((confirmed) => {
      if (confirmed) {
        this.chatService.removeAdmin(this.memberChannelId, member.userId).subscribe({
          next: (res) => {
            console.log("Member dismissed as admin:", res);
            const mIndex = this.allGroupMembers.findIndex(m => m.userId === member.userId);
            if (mIndex > -1) {
              this.allGroupMembers[mIndex].isAdmin = false;
              if (this.viewingMember && this.viewingMember.userId === member.userId) {
                this.viewingMember.isAdmin = false;
              }
            }
            this.emitAllGroupMembersChange();
            alertModalWithoutConfirm('success', 'Admin privileges removed');
          },
          error: (err) => {
            console.error("Failed to dismiss admin", err);
            alertModalWithoutConfirm('error', 'Failed to update admin status');
          }
        });
      }
    });
  }

  toggleMemberDetails(userId: string) {
    if (this.expandedMemberId === userId) {
      this.expandedMemberId = null;
    } else {
      this.expandedMemberId = userId;
    }
  }

  /** Only the Admin department deletes a chat for everyone (3 Nov 2026); the server checks it too. */
  get isLinkAdmin(): boolean {
    return (localStorage.getItem('department') || '').trim().toUpperCase() === 'ADMIN';
  }

  deleteGroupChat() {
    if (!this.isLinkAdmin) return;
    alertModalDelete(
      `Delete "${this.groupName}" for everyone?`,
      'The group and its messages go for every member. This cannot be undone.'
    ).then((confirmed) => {
      if (confirmed) {
        this.chatService.deleteGroupChat(this.memberChannelId).subscribe({
          next: (res) => {
            console.log("Group deleted:", res);
            alertModalWithoutConfirm('success', 'Group deleted successfully');
            this.groupDeleted.emit(this.memberChannelId);
            this.closeGroupInfoPanel();
          },
          error: (err) => {
            console.error("Failed to delete group:", err);
            alertModalWithoutConfirm('error', 'Failed to delete group');
          }
        });
      }
    });
  }

  /** Everyone else's delete: the group leaves your list and you leave it; the others keep it. */
  removeFromMyChats() {
    alertModalConfirm(
      `Remove "${this.groupName}" from your chats?`,
      'You leave the group and your copy of its messages is cleared. Everyone else keeps the group as it is.'
    ).then((confirmed) => {
      if (!confirmed) return;
      this.chatService.removeFromMyChats(this.memberChannelId).subscribe({
        next: () => {
          alertModalWithoutConfirm('success', 'Removed from your chats');
          this.groupDeleted.emit(this.memberChannelId);
          this.closeGroupInfoPanel();
        },
        error: (err) => {
          console.error('Failed to remove the group from my chats:', err);
          alertModalWithoutConfirm('error', 'Could not remove the group from your chats');
        },
      });
    });
  }

  getFilteredGroupMembers() {
    // Only active members
    let members = [...this.allGroupMembers].filter(
      (m) => !m.isRemoved && !m.leftGroup
    );

    // Sort: Admin on top
    members.sort((a, b) => {
      if (a.isAdmin && !b.isAdmin) return -1;
      if (!a.isAdmin && b.isAdmin) return 1;
      return 0;
    });

    if (!this.groupMemberSearchQuery) {
      return members;
    }
    const query = this.groupMemberSearchQuery.toLowerCase();
    return members.filter(
      (m) =>
        (m.userName && m.userName.toLowerCase().includes(query)) ||
        (m.displayName && m.displayName.toLowerCase().includes(query))
    );
  }

  editGroupInfo() {
    this.isEditGroupMode = true;
    this.newGroupName = this.groupChatName || this.groupName;
    this.newDiscription = this.groupChatDescription || "";
    this.newGroupImage = this.groupChatProfileImg;
    this.selectedGroupImageFile = null;
    setTimeout(() => {
      const textarea = document.querySelector('.edit-desc-input') as HTMLTextAreaElement;
      if (textarea) {
        this.autoGrowDescription(textarea);
      }
    }, 50);
  }

  onGroupImageSelected(event: any) {
    const file = event.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      alertModalWithoutConfirm('warning', 'Only image files are allowed for group profile photo.');
      event.target.value = '';
      return;
    }
    this.selectedGroupImageFile = file;
    const reader = new FileReader();
    reader.onload = (e: any) => {
      this.newGroupImage = e.target.result;
    };
    reader.readAsDataURL(file);
  }

  updateGroupInfo() {
    if (!this.newGroupName) {
      alertModalWithoutConfirm('warning', 'Group name is required');
      return;
    }
    if (this.newGroupName.length > 100) {
      alertModalWithoutConfirm('warning', 'Group name cannot exceed 100 characters');
      return;
    }
    this.isCreatingGroup = true;
    // Normalize strings for comparison
    const currentName = (this.groupName || "").trim();
    const newName = (this.newGroupName || "").trim();
    const currentDesc = (this.groupChatDescription || "").trim();
    const newDesc = (this.newDiscription || "").trim();
    let hasChanges = false;
    const formData = new FormData();
    if (newName !== currentName) {
      formData.append('groupName', newName);
      hasChanges = true;
    }
    if (newDesc !== currentDesc) {
      formData.append('groupDescription', newDesc === "" ? " " : newDesc);
      hasChanges = true;
    }

    if (this.selectedGroupImageFile) {
      formData.append('file', this.selectedGroupImageFile);
      hasChanges = true;
    }
    if (!hasChanges) {
      this.isCreatingGroup = false;
      this.isEditGroupMode = false;
      return;
    }

    this.chatService.editGroup(formData, this.memberChannelId).subscribe({
      next: (res: any) => {
        this.isCreatingGroup = false;
        const updatedGroup = res.data || res;
        // Update parent component's properties if needed
        alertModalWithoutConfirm('success', 'Group updated successfully');
        this.isEditGroupMode = false;

        // Emit changes to parent
        this.groupName = newName;
        this.groupChatName = newName;
        this.groupChatDescription = newDesc;
        this.groupNameChange.emit(this.groupName);
        this.groupChatNameChange.emit(this.groupChatName);
        this.groupChatDescriptionChange.emit(this.groupChatDescription);

        if (updatedGroup?.groupImg) {
          this.groupChatProfileImg = updatedGroup.groupImg;
          this.groupChatProfileImgChange.emit(this.groupChatProfileImg);
        }
      },
      error: (err) => {
        console.error("Failed to update group:", err);
        this.isCreatingGroup = false;
        alertModalWithoutConfirm('error', 'Failed to update group');
      }
    });
  }

  fetchAvailableUsersForAdd() {
    this.chatLoader = true;
    this.chatService.getChats().subscribe({
      next: (resp: any) => {
        const users = resp.data || [];
        const currentMemberIds = this.allGroupMembers.filter(m => !m.isRemoved && !m.leftGroup).map(m => m.userId);
        this.availableUsersForAdd = users
          .filter((u: any) =>
            u.type !== 'group' &&
            u.userId !== this.userId &&
            !currentMemberIds.includes(u.userId)
          )
          .sort((a: any, b: any) => {
            const nameA = (a.displayName || a.userName || '').toLowerCase();
            const nameB = (b.displayName || b.userName || '').toLowerCase();
            return nameA.localeCompare(nameB);
          });
        this.chatLoader = false;
      },
      error: (err) => {
        console.error("Failed to fetch users", err);
        this.chatLoader = false;
      }
    });
  }

  addSelectedMembersToGroup() {
    if (this.selectedNewMembers.length === 0) return;

    this.isAddingMember = true;
    const membersToAdd = this.selectedNewMembers.map(u => ({
      userId: u.userId,
      displayName: u.displayName || u.userName,
      userName: u.userName,
      profileImage: u.profileImage
    }));

    const payload = {
      channelId: this.memberChannelId,
      members: membersToAdd
    };

    this.chatService.addMemberToChannel(payload).subscribe({
      next: (res: any) => {
        console.log("Members added:", res);
        // Prevent duplicates from race condition with socket event
        membersToAdd.forEach(member => {
          const existingMember = this.allGroupMembers.find(m => m.userId === member.userId);
          if (!existingMember) {
            this.allGroupMembers.push(member);
          } else {
            // Reactivate if needed
            existingMember.isRemoved = false;
            existingMember.leftGroup = false;
            existingMember.exitedAt = null;
          }
        });
        this.emitAllGroupMembersChange();
        this.selectedNewMembers = [];
        this.isAddingMember = false;
        alertModalWithoutConfirm('success', 'Members added successfully');
        this.backToGroupInfo();
      },
      error: (err) => {
        console.error("Failed to add members:", err);
        this.isAddingMember = false;
        alertModalWithoutConfirm('error', 'Failed to add members');
      }
    });
  }

  toggleNewMemberSelection(user: any) {
    const index = this.selectedNewMembers.findIndex(m => m.userId === user.userId);
    if (index > -1) {
      this.selectedNewMembers.splice(index, 1);
    } else {
      this.selectedNewMembers.push(user);
    }
  }

  exitGroup() {
    this.performExitGroup();
  }

  performExitGroup() {
    alertModalExit(`Are you sure you want to exit "${this.groupName}"?`).then((confirmed) => {
      if (confirmed) {
        this.chatService.exitGroup(this.memberChannelId).subscribe({
          next: (res: any) => {
            console.log("Exited group:", res);
            this.exitedGroups.add(this.memberChannelId);
            this.emitExitedGroupsChange();
            this.isEditGroupMode = false;
            this.isGroupInfoOpen = false;
            this.emitIsGroupInfoOpenChange();
            this.groupExited.emit(this.memberChannelId);
            alertModalWithoutConfirm('success', 'You have exited the group');
          },
          error: (err) => {
            console.error("Failed to exit group:", err);
            alertModalWithoutConfirm('error', 'Failed to exit group');
          }
        });
      }
    });
  }

  makeMemberAdmin(member: any) {
    alertModalConfirm(`Make ${member.displayName} an admin?`).then((confirmed) => {
      if (confirmed) {
        this.chatService.makeGroupAdmin(this.memberChannelId, member.userId).subscribe({
          next: (res) => {
            console.log("Member promoted to admin:", res);
            const mIndex = this.allGroupMembers.findIndex(m => m.userId === member.userId);
            if (mIndex > -1) {
              this.allGroupMembers[mIndex].isAdmin = true;
              if (this.viewingMember && this.viewingMember.userId === member.userId) {
                this.viewingMember.isAdmin = true;
              }
            }
            this.emitAllGroupMembersChange();
            alertModalWithoutConfirm('success', 'Admin privileges granted');
          },
          error: (err) => {
            console.error("Failed to make admin", err);
            alertModalWithoutConfirm('error', 'Failed to update admin status');
          }
        });
      }
    });
  }

  removeMemberFromGroup(member: any) {

    const payload = {
      memberId: member.userId,
      channelId: this.memberChannelId,
    }

    alertModalRemove(`Remove ${member.displayName || member.userName} from group?`).then((confirmed) => {
      if (confirmed) {
        this.chatService.removeMemberFromChannel(payload).subscribe({
          next: (res) => {
            console.log("Member removed:", res);
            const mIndex = this.allGroupMembers.findIndex(m => m.userId === member.userId);
            if (mIndex > -1) {
              this.allGroupMembers[mIndex].isRemoved = true;
            }
            this.emitAllGroupMembersChange();
            alertModalWithoutConfirm('success', 'Member removed from group');
            this.backToGroupInfo();
          },
          error: (err) => {
            console.error("Failed to remove member:", err);
            alertModalWithoutConfirm('error', 'Failed to remove member');
          }
        });
      }
    });
  }

  clearChat() {
    alertModalClearChat(`Clear chat history for "${this.groupName}"?`).then((confirmed) => {
      if (confirmed) {
        this.chatService.clearChat(this.memberChannelId).subscribe({
          next: (res) => {
            console.log("Chat cleared:", res);
            this.chatCleared.emit();
            alertModalWithoutConfirm('success', 'Chat history cleared');
          },
          error: (err) => {
            console.error("Failed to clear chat:", err);
            alertModalWithoutConfirm('error', 'Failed to clear chat');
          }
        });
      }
    });
  }

  onMessage(member: any) {
    this.startSingleChat.emit(member.userId);
    this.closeGroupInfoPanel();
  }

  emitAllGroupMembersChange() {
    this.allGroupMembersChange.emit(this.allGroupMembers);
  }

  emitExitedGroupsChange() {
    this.exitedGroupsChange.emit(this.exitedGroups);
  }

  handleImageError(event: any) {
    event.target.src = 'assets/user-icon.png';
  }

  handleGroupImageError(event: any) {
    event.target.src = 'assets/group_chat.png';
  }

  isAdmin(userId: any): boolean {
    if (!this.allGroupMembers) return false;
    const member = this.allGroupMembers.find(m => m.userId === userId);
    return member ? member.isAdmin : false;
  }

  // --- Shared Content Extraction & Methods ---
  extractSharedContent(sourceMessages?: any[]) {
    const media: any[] = [];
    const files: any[] = [];
    const audio: any[] = [];
    const gifs: any[] = [];
    const links: any[] = [];

    const seenMediaUrls = new Set<string>();
    const seenFileUrls = new Set<string>();
    const seenAudioUrls = new Set<string>();
    const seenGifUrls = new Set<string>();
    const seenLinks = new Set<string>();

    const messages = sourceMessages || this.chatMessages || [];

    for (const msg of messages) {
      if (!msg) continue;
      const meta = getMessageMeta(msg);
      const createdAt = msg.createdAt || msg.date || msg.time;

      // 1. Check for GIFs
      if (isGifMessage(msg)) {
        const gifUrl = meta.gifUrl || meta.fileUrl || meta.url || msg.url || msg.fileUrl || (typeof msg.message === 'string' && msg.message.startsWith('http') ? msg.message : '');
        if (gifUrl && !seenGifUrls.has(gifUrl)) {
          seenGifUrls.add(gifUrl);
          gifs.push({
            type: 'gif',
            url: gifUrl,
            previewUrl: meta.previewUrl || gifUrl,
            date: createdAt,
            message: msg
          });
        }
        continue;
      }

      // Check text for URLs / Links
      const text = typeof msg.message === 'string' ? msg.message : '';
      const extractedUrls = extractUrls(text);
      if (extractedUrls && extractedUrls.length > 0) {
        for (const u of extractedUrls) {
          if (!seenLinks.has(u)) {
            seenLinks.add(u);
            links.push({
              url: u,
              domain: this.getLinkDomain(u),
              title: meta.title || '',
              description: meta.description || '',
              image: meta.image || meta.icon || '',
              date: createdAt,
              message: msg
            });
          }
        }
      } else if (isURL(text) || meta.title || (meta.image && !msg.fileUrl && !msg.files)) {
        const linkUrl = text || meta.url || '';
        if (linkUrl && !seenLinks.has(linkUrl)) {
          seenLinks.add(linkUrl);
          links.push({
            url: linkUrl,
            domain: this.getLinkDomain(linkUrl),
            title: meta.title || '',
            description: meta.description || '',
            image: meta.image || meta.icon || '',
            date: createdAt,
            message: msg
          });
        }
      }

      // 2. Check for Voice messages / Audio in message
      if (isVoiceMessage(msg) || isAudio(msg)) {
        const rawAudioUrl = msg.url || msg.fileUrl || meta.fileUrl || meta.url || (text.startsWith('http') ? text : '');
        const audioUrl = Array.isArray(rawAudioUrl) ? rawAudioUrl[0] : rawAudioUrl;
        if (audioUrl && !seenAudioUrls.has(audioUrl)) {
          seenAudioUrls.add(audioUrl);
          audio.push({
            type: 'audio',
            isVoice: isVoiceMessage(msg),
            url: audioUrl,
            name: meta.ogFileName || (isVoiceMessage(msg) ? 'Voice message' : 'Audio recording'),
            duration: meta.duration || meta.voiceDuration || '',
            date: createdAt,
            message: msg
          });
          continue;
        }
      }

      // 3. Extract attachments with getFilesFromMessage
      const msgFiles = getFilesFromMessage(msg);
      if (msgFiles && msgFiles.length > 0) {
        msgFiles.forEach((f, idx) => {
          const fileUrl = f.fileUrl || f.url;
          if (!fileUrl) return;

          // Check if GIF
          if (fileUrl.toLowerCase().includes('.gif') || fileUrl.toLowerCase().includes('giphy') || fileUrl.toLowerCase().includes('tenor')) {
            if (!seenGifUrls.has(fileUrl)) {
              seenGifUrls.add(fileUrl);
              gifs.push({
                type: 'gif',
                url: fileUrl,
                previewUrl: fileUrl,
                date: createdAt,
                message: msg
              });
            }
            return;
          }

          // Check if Audio
          if (isAudio(fileUrl, f.name) || isVoiceMessage(fileUrl) || f.type?.startsWith('audio/')) {
            if (!seenAudioUrls.has(fileUrl)) {
              seenAudioUrls.add(fileUrl);
              audio.push({
                type: 'audio',
                isVoice: isVoiceMessage(fileUrl),
                url: fileUrl,
                name: f.name || (isVoiceMessage(fileUrl) ? 'Voice message' : 'Audio file'),
                duration: meta.duration || '',
                date: createdAt,
                message: msg
              });
            }
            return;
          }

          // Check if Media (Photo or Video)
          if (isImage(fileUrl)) {
            if (!seenMediaUrls.has(fileUrl)) {
              seenMediaUrls.add(fileUrl);
              media.push({
                type: 'photo',
                url: fileUrl,
                thumbnailUrl: fileUrl,
                name: f.name || 'Photo',
                date: createdAt,
                message: msg,
                fileIndex: idx
              });
            }
            return;
          }

          if (isVideo(fileUrl) || f.type?.startsWith('video/')) {
            if (!seenMediaUrls.has(fileUrl)) {
              seenMediaUrls.add(fileUrl);
              media.push({
                type: 'video',
                url: fileUrl,
                thumbnailUrl: meta.thumbnailUrl || fileUrl,
                name: f.name || 'Video',
                date: createdAt,
                message: msg,
                fileIndex: idx
              });
            }
            return;
          }

          // Document / File
          if (!seenFileUrls.has(fileUrl)) {
            seenFileUrls.add(fileUrl);
            const ext = this.extractExtension(fileUrl, f.name);
            files.push({
              type: 'file',
              url: fileUrl,
              name: f.name || this.getCleanName(fileUrl),
              extension: ext,
              label: this.getExtensionLabel(ext),
              color: this.getExtensionColor(ext),
              size: f.size || meta.fileSize || '',
              date: createdAt,
              message: msg,
              file: f
            });
          }
        });
      }
    }

    this.sharedMedia = media.reverse();
    this.sharedFiles = files.reverse();
    this.sharedAudio = audio.reverse();
    this.sharedGifs = gifs.reverse();
    this.sharedLinks = links.reverse();
  }

  fetchSharedHistory(channelId: string) {
    if (!channelId) return;
    this.chatService.searchChatMessages({
      chatIds: [channelId],
      limit: 100,
      pageNum: 1
    }).subscribe({
      next: (res: any) => {
        const msgs = res?.data || res?.messages || res?.rows || (Array.isArray(res) ? res : []);
        if (msgs && msgs.length > 0) {
          const currentList = this.chatMessages || [];
          const existingIds = new Set(currentList.map(m => m.id || m._id || m.message_id));
          const newMsgs = msgs.filter((m: any) => {
            const id = m.id || m._id || m.message_id;
            return id ? !existingIds.has(id) : true;
          });
          if (newMsgs.length > 0) {
            this.extractSharedContent([...currentList, ...newMsgs]);
          }
        }
      },
      error: (err) => {
        console.debug('fetchSharedHistory info:', err);
      }
    });
  }

  onMediaClick(item: any) {
    if (item.message) {
      this.mediaClick.emit({ message: item.message, index: item.fileIndex || 0 });
    }
  }

  onDownloadFile(item: any, event?: MouseEvent) {
    if (event) event.stopPropagation();
    if (item?.url) {
      downloadFile(item.url, item.name);
    }
  }

  onPreviewFile(item: any, event?: MouseEvent) {
    if (event) event.stopPropagation();
    if (item?.url) {
      openDocumentPreview(this.dialog, { fileUrl: item.url, name: item.name, sizeLabel: item.size });
    }
  }

  onGifClick(item: any) {
    if (item.message) {
      this.mediaClick.emit({ message: item.message, index: 0 });
    } else if (item.url) {
      window.open(item.url, '_blank');
    }
  }

  onLinkClick(item: any, event?: MouseEvent) {
    if (event) event.stopPropagation();
    if (!item?.url) return;
    const url = item.url.startsWith('http') ? item.url : `https://${item.url}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  toggleAudioPlayback(item: any) {
    if (!item?.url) return;

    if (this.currentPlayingAudioUrl === item.url && this.isPlayingAudio) {
      this.pauseAudio();
      return;
    }

    if (this.audioPlayer) {
      this.audioPlayer.pause();
      this.audioPlayer = null;
    }

    this.currentPlayingAudioUrl = item.url;
    this.isPlayingAudio = true;
    this.audioPlayer = new Audio(item.url);

    this.audioPlayer.ontimeupdate = () => {
      if (this.audioPlayer && this.audioPlayer.duration) {
        this.audioProgress = (this.audioPlayer.currentTime / this.audioPlayer.duration) * 100;
        const mins = Math.floor(this.audioPlayer.currentTime / 60);
        const secs = Math.floor(this.audioPlayer.currentTime % 60);
        this.audioCurrentTime = `${mins}:${secs < 10 ? '0' : ''}${secs}`;
      }
    };

    this.audioPlayer.onended = () => {
      this.isPlayingAudio = false;
      this.currentPlayingAudioUrl = null;
      this.audioProgress = 0;
      this.audioCurrentTime = '0:00';
    };

    this.audioPlayer.onerror = () => {
      this.isPlayingAudio = false;
      this.currentPlayingAudioUrl = null;
      this.audioProgress = 0;
    };

    this.audioPlayer.play().catch(e => {
      console.warn('Audio play failed:', e);
      this.isPlayingAudio = false;
      this.currentPlayingAudioUrl = null;
    });
  }

  pauseAudio() {
    if (this.audioPlayer) {
      this.audioPlayer.pause();
    }
    this.isPlayingAudio = false;
  }

  stopAudioPlayback() {
    if (this.audioPlayer) {
      this.audioPlayer.pause();
      this.audioPlayer = null;
    }
    this.isPlayingAudio = false;
    this.currentPlayingAudioUrl = null;
    this.audioProgress = 0;
    this.audioCurrentTime = '0:00';
  }

  onClearSingleChat() {
    this.clearSingleChat.emit();
  }

  formatDate(date: any): string {
    if (!date) return '';
    try {
      const d = new Date(date);
      if (isNaN(d.getTime())) return '';
      const now = new Date();
      const isToday = d.toDateString() === now.toDateString();
      if (isToday) {
        return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      }
      return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    } catch {
      return '';
    }
  }

  getLinkDomain(url: string): string {
    if (!url) return '';
    try {
      const u = new URL(url.startsWith('http') ? url : `https://${url}`);
      return u.hostname.replace(/^www\./, '');
    } catch {
      return url;
    }
  }

  extractExtension(url: string, name?: string): string {
    const target = name || url || '';
    const clean = target.split('?')[0];
    const parts = clean.split('.');
    return parts.length > 1 ? parts.pop()!.toLowerCase() : 'file';
  }

  getExtensionLabel(ext: string): string {
    const map: Record<string, string> = {
      pdf: 'PDF', doc: 'DOC', docx: 'DOC',
      xls: 'XLS', xlsx: 'XLS', csv: 'CSV',
      ppt: 'PPT', pptx: 'PPT', txt: 'TXT',
      zip: 'ZIP', rar: 'RAR', '7z': '7Z',
      mp3: 'MP3', wav: 'WAV', json: 'JSON'
    };
    return map[ext] || ext.toUpperCase();
  }

  getExtensionColor(ext: string): string {
    const map: Record<string, string> = {
      pdf: '#E53E3E',
      doc: '#2B6CB0', docx: '#2B6CB0',
      xls: '#276749', xlsx: '#276749', csv: '#276749',
      ppt: '#C05621', pptx: '#C05621',
      txt: '#718096',
      zip: '#D69E2E', rar: '#D69E2E', '7z': '#D69E2E'
    };
    return map[ext] || '#6565ac';
  }

  getCleanName(url: string): string {
    if (!url) return 'Document';
    const base = url.split('?')[0].split('/').pop() || url;
    return decodeURIComponent(base.replace(/^(\d{10,}[-_])+/, ''));
  }
}